import { CsvBulkConfig, CsvFieldDefinition } from 'components/csv/types';
import { CreateAssessmentParams, createAssessment } from 'stores/AssessmentStoreAdapter';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';

/**
 * 資産CSV一括登録用のフィールド定義を生成する
 */
export const createAssessmentCsvFields = (
  assessmentTypes: MasterTypeResult[],
  users: UserResult[],
): CsvFieldDefinition[] => [
  {
    header: '資産種別コード',
    key: 'assessmentTypeCode',
    description: '資産種別のコード値（必須）',
    required: true,
    type: 'select',
    options: assessmentTypes.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '資産名',
    key: 'assessmentName',
    description: '資産名（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '管理番号1',
    key: 'managementNumber1',
    description: '管理番号1（任意、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '管理番号2',
    key: 'managementNumber2',
    description: '管理番号2（任意、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '管理番号3',
    key: 'managementNumber3',
    description: '管理番号3（任意、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '利用者ID',
    key: 'userId',
    description: '利用者のユーザーID（必須）',
    required: true,
    type: 'id',
    options: users.map((u) => ({ code: u.userId, label: u.userName })),
  },
  {
    header: '管理者ID',
    key: 'managerId',
    description: '管理者のユーザーID（必須）',
    required: true,
    type: 'id',
    options: users.map((u) => ({ code: u.userId, label: u.userName })),
  },
  {
    header: '情報1',
    key: 'information1',
    description: '情報1（任意、最大1000文字）',
    required: false,
    type: 'text',
    maxLength: 1000,
  },
  {
    header: '情報2',
    key: 'information2',
    description: '情報2（任意、最大1000文字）',
    required: false,
    type: 'text',
    maxLength: 1000,
  },
  {
    header: '情報3',
    key: 'information3',
    description: '情報3（任意、最大1000文字）',
    required: false,
    type: 'text',
    maxLength: 1000,
  },
  {
    header: '備考',
    key: 'note',
    description: '備考（任意、最大10000文字）',
    required: false,
    type: 'text',
    maxLength: 10000,
  },
];

/**
 * 資産CSV一括登録のConfig生成関数
 */
export const createAssessmentBulkConfig = (
  assessmentTypes: MasterTypeResult[],
  users: UserResult[],
): CsvBulkConfig<CreateAssessmentParams> => {
  const fields = createAssessmentCsvFields(assessmentTypes, users);

  return {
    entityName: '資産',
    fields,
    toApiParams: (row: Record<string, string>): CreateAssessmentParams => {
      const params: CreateAssessmentParams = {
        assessmentTypeCode: row.assessmentTypeCode,
        assessmentName: row.assessmentName,
        userId: row.userId,
        managerId: row.managerId,
      };

      if (row.managementNumber1) params.managementNumber1 = row.managementNumber1;
      if (row.managementNumber2) params.managementNumber2 = row.managementNumber2;
      if (row.managementNumber3) params.managementNumber3 = row.managementNumber3;
      if (row.information1) params.information1 = row.information1;
      if (row.information2) params.information2 = row.information2;
      if (row.information3) params.information3 = row.information3;
      if (row.note) params.note = row.note;

      return params;
    },
    registerOne: createAssessment,
    resolveDisplayValue: (key: string, rawValue: string): string | null => {
      if (!rawValue) return null;

      const field = fields.find((f) => f.key === key);
      if (!field || !field.options) return null;

      const option = field.options.find((o) => o.code === rawValue);
      return option ? option.label : null;
    },
    validateField: (key: string, value: string, _row: Record<string, string>): string | null => {
      // 利用者IDの存在チェック
      if (key === 'userId' && value) {
        const userExists = users.some((u) => u.userId === value);
        if (!userExists) return `利用者ID「${value}」は存在しません`;
      }

      // 管理者IDの存在チェック
      if (key === 'managerId' && value) {
        const managerExists = users.some((u) => u.userId === value);
        if (!managerExists) return `管理者ID「${value}」は存在しません`;
      }

      return null;
    },
  };
};
